import { Component, DestroyRef, OnInit, inject, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  UntypedFormGroup,
  Validators,
  UntypedFormBuilder,
  ReactiveFormsModule,
} from '@angular/forms';
import { ICities } from '../../../../../@interfaces/cities.interface';
import { IIcaBase } from '../../../../../@interfaces/icaBase.interface';
import { ITypes } from '../../../../../@interfaces/types.interface';
import { TributaryService } from '../../../../../@pages/tributary/tributary.service';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { NgxSpinnerService, NgxSpinnerModule } from 'ngx-spinner';
import { Router } from '@angular/router';
import { CookieAuthService } from 'src/app/@shared/storage-variables/cookie-auth.service';
import { IStorageCompany } from 'src/app/@interfaces/company.interface';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { AlertService } from '@shared-services';

@Component({
  selector: 'app-modify-ica-bases',
  templateUrl: './modify-ica-bases.component.html',
  styleUrls: ['./modify-ica-bases.component.css'],
  imports: [
    MatDialogModule,
    ReactiveFormsModule,
    MatButtonModule,
    NgxSpinnerModule,
    TranslatePipe,
  ],
})
export class ModifyIcaBasesComponent implements OnInit {
  private fb = inject(UntypedFormBuilder);
  private tributaryService = inject(TributaryService);
  private translate = inject(TranslateService);
  private spinnerService = inject(NgxSpinnerService);
  private router = inject(Router);
  private cookieAuthService = inject(CookieAuthService);
  private alertService = inject(AlertService);
  private destroyRef = inject(DestroyRef);

  readonly icaBase = input<IIcaBase>({} as IIcaBase);

  icaBaseForm: UntypedFormGroup = this.fb.group({
    uvt: ['', [Validators.required]],
  });
  cityName = '';
  typeDescription = '';
  companyObject = {} as IStorageCompany;

  ngOnInit(): void {
    const row = this.icaBase();
    this.companyObject = this.cookieAuthService.getCompanyObject!;
    this.icaBaseForm.patchValue({ uvt: row.uvt });
    const country_id = this.companyObject.countries[0].id_country;
    const targetCity = Number(row.id_city);
    const targetType = Number(row.id_type);
    this.tributaryService
      .getCitiesByCompany(parseInt(country_id!.toString()))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: ICities[]) => {
        const match = (result || []).find(
          (city) => Number(city.id_city) === targetCity,
        );
        this.cityName = match ? match.name : '';
      });
    this.tributaryService
      .getAllTypes()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: ITypes[]) => {
        const match = (result || []).find(
          (type) => Number(type.id_type) === targetType,
        );
        this.typeDescription = match ? match.description : '';
      });
  }

  textValid(text: string) {
    return (
      this.icaBaseForm.get(text)?.invalid && this.icaBaseForm.get(text)?.touched
    );
  }

  update() {
    this.icaBaseForm.markAllAsTouched();
    if (this.icaBaseForm.invalid === true) {
      this.openAlert(
        'error',
        'tributary.common.error',
        'tributary.icaBases.messages.requiredFields',
      );
      return;
    }
    this.spinnerService.show();
    this.saveOnDataBase();
  }

  saveOnDataBase() {
    this.tributaryService
      .updateIcaBase(
        this.icaBase().id_ica_base,
        Number(this.icaBaseForm.value.uvt),
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.spinnerService.hide();
          if (!result) {
            this.openAlert(
              'error',
              'tributary.common.error',
              'tributary.icaBases.messages.genericError',
            );
            return;
          }
          this.alertService
            .open({
              title: this.translate.instant('tributary.common.success'),
              message: this.translate.instant(
                'tributary.icaBases.messages.updated',
              ),
              showClose: true,
              type: 'success',
            })
            .subscribe({
              accept: () => this.refreshTributary(),
              close: () => this.refreshTributary(),
            });
        },
        error: () => {
          this.spinnerService.hide();
          this.openAlert(
            'error',
            'tributary.common.error',
            'tributary.icaBases.messages.genericError',
          );
        },
      });
  }

  private openAlert(
    type: 'success' | 'error' | 'warning',
    titleKey: string,
    messageKey: string,
  ): void {
    this.alertService.open({
      title: this.translate.instant(titleKey),
      message: this.translate.instant(messageKey),
      showClose: true,
      type,
    });
  }

  private refreshTributary(): void {
    this.router
      .navigateByUrl('/table', { skipLocationChange: true })
      .then(() => {
        this.router.navigate(['/tributary']);
      });
  }
}
